// src/components/SkillsSection.jsx
"use client";
import React from "react";
import {
  FaToolbox,       // heading icon
  FaCode,          // Programming & Scripting
  FaBrain,         // Data-Science / ML
  FaServer,        // Data-Engineering & Big-Data
  FaCogs,          // DevOps / Cloud
  FaProjectDiagram,// Front-End & Visualisation
  FaMicrochip,     // Electrical / Hardware
  FaUsers          // Professional & Interpersonal
} from "react-icons/fa";

import styles from "./SkillsSection.module.css";

/* ------------------------------------------------------------------
   SKILLS — reorganised into 7 balanced categories
------------------------------------------------------------------- */
const skillsData = [
  /* 1 ──────────────────────────────────────────────────────────── */
  {
    category: "Programming & Scripting",
    icon: <FaCode />,
    skills: [
      "Python (pandas, NumPy, scikit‑learn, TensorFlow, PyTorch, FastAPI)",
      "R (tidyverse, rstan, survival, Bayesian NMA)",
      "C & C++ (C++17 STL, multithreading, embedded)",
      "JavaScript / TypeScript",
      "HTML & CSS (TailwindCSS)",
      "Bash & PowerShell",
      "PineScript",
      "Git / GitHub / GitLab workflows"
    ]
  },

  /* 2 ──────────────────────────────────────────────────────────── */
  {
    category: "Data Science, Statistics & ML",
    icon: <FaBrain />,
    skills: [
      "Regression • Classification • Clustering",
      "Survival analysis & Bayesian inference",
      "Deep Learning (ResNet, YOLO, TensorRT, Transfer Learning)",
      "Computer Vision & Image Processing (OpenCV, PIL)",
      "Feature engineering, A/B testing, model monitoring",
      "Algorithmic & AI‑based trading strategies",
      "Evidence synthesis (meta-analysis, Bayesian NMA)",
      "Data visualisation (Matplotlib, Seaborn, ggplot2, Plotly, Grafana)",
      "Advanced Excel (Power‑Query, VBA)"
    ]
  },

  /* 3 ──────────────────────────────────────────────────────────── */
  {
    category: "Data Engineering & Big Data",
    icon: <FaServer />,
    skills: [
      "Apache Airflow DAG design & orchestration",
      "Apache Spark • Databricks",
      "ETL pipelines, data‑quality checks, schema versioning",
      "RESTful API design & integration",
      "SQL (PostgreSQL) – schema design & optimisation",
      "NoSQL (MongoDB, InfluxDB)",
      "Data governance, GDPR & security awareness"
    ]
  },

  /* 4 ──────────────────────────────────────────────────────────── */
  {
    category: "DevOps, Cloud & Tooling",
    icon: <FaCogs />,
    skills: [
      "AWS (EC2, S3, Lambda, IAM, CloudFormation)",
      "Docker & Compose • Kubernetes",
      "CI/CD – GitLab CI, GitHub Actions, Jenkins",
      "Terraform • HashiCorp stack",
      "Prometheus, Grafana, Splunk",
      "Linux & Windows administration",
      "Security best practices & compliance",
      "Issue tracking (Jira, Trello) • Test automation (pytest, GoogleTest, linting)"
    ]
  },

  /* 5 ──────────────────────────────────────────────────────────── */
  {
    category: "Front‑End & Visualisation",
    icon: <FaProjectDiagram />,
    skills: [
      "React • Next.js • Vue.js • D3.js",
      "Three.js & GSAP animations",
      "Responsive UI / dashboard design",
      "TailwindCSS & Styled‑Components",
      "WordPress + Elementor for rapid marketing sites",
      "Grafana dashboards • Plotly & ggplot2 charts",
      "HTML, CSS, JavaScript"
    ]
  },

  /* 6 ──────────────────────────────────────────────────────────── */
  {
    category: "Electrical Engineering & Hardware",
    icon: <FaMicrochip />,
    skills: [
      "Circuit design & simulation",
      "Embedded systems (Arduino, Raspberry Pi, ESP8266)",
      "Sensor integration (temperature, proximity, IR, LCD)",
      "Power electronics (capacitors, amplifiers, signal processing)",
      "PCB design & layout",
      "Measurement & test‑equipment interfacing"
    ]
  },

  /* 7 ──────────────────────────────────────────────────────────── */
  {
    category: "Professional & Interpersonal Skills",
    icon: <FaUsers />,
    skills: [
      "Analytical problem‑solving & root‑cause analysis",
      "Clear technical writing & stakeholder storytelling",
      "Critical thinking • Attention to detail",
      "Creativity, flexibility & adaptability",
      "Agile / Scrum teamwork – sprint demos, backlog grooming",
      "Project & time management across concurrent projects",
      "Leadership & resilience (Royal Marines Cadets)",
      "Mentoring peers & supporting non‑technical users"
    ]
  }
];

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------- */
export default function SkillsSection() {
  return (
    <section className={styles.skillsSection} id="skills">
      <div className={styles.heading}>
        <FaToolbox className={styles.headingIcon} />
        <h3>SKILLS</h3>
      </div>

      <div className={styles.gridContainer}>
        {skillsData.map((cat, i) => (
          <div key={i} className={styles.skillCard}>
            <h4 className={styles.categoryTitle}>
              {cat.icon} {cat.category}
            </h4>

            <ul className={styles.skillList}>
              {cat.skills.map((skill, j) => (
                <li key={j} className={styles.skillItem}>
                  {skill}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
